import { toRecord, parseJsonIfString, getString, type GenericRecord } from '@/utils/jsonHelpers';
import { getFileExtension } from '@/utils/vaultItemMapping';
import type {
  HumanInTheLoopDetail,
  HumanInTheLoopDetailFile,
  HumanInTheLoopDetailValue,
} from '@/components/humanInTheLoop/HumanInTheLoopActionCard.vue';

/**
 * Parses the four interrupt payload shapes the backend can send inside a
 * `chat_interrupt` websocket frame into one common model the HITL UI renders.
 *
 * A free-text option is NOT uniform across shapes — it's a backend contract
 * detail, not a UI nicety, so don't "fix" the asymmetry below:
 *  - tool_confirmation: "Chat about this", always appended. The backend accepts
 *    a bare `{message}` answer (no `choice`) as a valid clarification/deny-with-
 *    feedback response regardless of what `choices` were offered.
 *  - user_question: "Answer in your own words…", always appended, for the same
 *    reason — `UserQuestionAnswer.message` stands alone when the user answers
 *    off-menu instead of picking one of the offered `options`.
 *  - legacy_action: appended only when the backend's `review_configs` allow a
 *    `reject` decision for that action — it reuses the real `reject` decision
 *    type (with an optional message), it isn't synthetic.
 *  - langgraph_action: never synthesized. LangGraph's resume contract is a
 *    bespoke `{action, ...input}` per graph node with no generic
 *    clarify-without-picking-an-action affordance the frontend can safely
 *    invent. Negative-sounding actions (reject/deny/cancel/ignore) are simply
 *    sorted last so they occupy the trailing slot visually.
 */

export type HitlShape =
  'legacy_action' | 'langgraph_action' | 'tool_confirmation' | 'user_question';
export type HitlStatus = 'pending' | 'submitting' | 'resolved';
export type NormalizedChoiceKind = 'plain' | 'edit-json' | 'schema-form' | 'chat';
export type NormalizedFieldKind =
  | 'select'
  | 'switch'
  | 'number'
  | 'json-array'
  | 'json-object'
  | 'text'
  | 'textarea'
  | 'library-collection';

export const CHAT_CHOICE_KEY = '__chat__';

export interface NormalizedFormField {
  name: string;
  label: string;
  kind: NormalizedFieldKind;
  required: boolean;
  default?: unknown;
  enumOptions?: { label: string; value: string }[];
  description?: string;
}

export interface NormalizedFormSchema {
  fields: NormalizedFormField[];
}

export interface NormalizedChoice {
  key: string;
  label: string;
  kind: NormalizedChoiceKind;
  isChatOption?: boolean;
  schema?: NormalizedFormSchema | null;
  /** user_question only: assistant-authored preview text for what picking this means. */
  data?: string | null;
}

/**
 * How picking a `plain` choice resolves:
 *  - `instant`: the click IS the answer, submitted immediately (every approval shape).
 *  - `confirm`: the click selects, and an explicit Confirm submits — needed when the
 *    choice carries `data` the user is meant to read before committing.
 */
export type HitlSelectionMode = 'instant' | 'confirm';

export interface NormalizedInterruptRequest {
  requestKey: string;
  title: string;
  description: string;
  details: HumanInTheLoopDetail[];
  choices: NormalizedChoice[];
  allowFreeform: boolean;
  selectionMode: HitlSelectionMode;
  /** legacy_action only: the raw (untitled) action name, needed for `edited_action.name`. */
  actionName?: string;
}

export interface NormalizedInterrupt {
  interruptId: string;
  shape: HitlShape;
  requests: NormalizedInterruptRequest[];
  status: HitlStatus;
  errorMessage: string | null;
  /** langgraph_action only: the LangGraph payload's own business `type` tag
   *  (e.g. "report_generation_confirm"), which host components key off of —
   *  see GaiaChatView's `interrupt-decision` emit. Not the same as `shape`. */
  businessType: string | null;
}

export interface HitlChoiceSubmission {
  choiceKey: string | null;
  message: string | null;
  input: Record<string, unknown> | null;
  editedArgs: Record<string, unknown> | null;
}

// ── shared helpers (ported from the legacy parser + LangGraphInterruptPanel) ──

type LegacyDecisionType = 'approve' | 'edit' | 'reject';
const LEGACY_DECISIONS = new Set<LegacyDecisionType>(['approve', 'edit', 'reject']);
const DEFAULT_LEGACY_DECISIONS: LegacyDecisionType[] = ['approve', 'edit', 'reject'];

function toLegacyDecision(value: unknown): LegacyDecisionType | null {
  if (typeof value !== 'string') return null;
  return LEGACY_DECISIONS.has(value as LegacyDecisionType) ? (value as LegacyDecisionType) : null;
}

function normalizeAllowedDecisions(value: unknown): LegacyDecisionType[] {
  const candidate = Array.isArray(value) ? value : [];
  const normalized = candidate
    .map(item => toLegacyDecision(item))
    .filter((item): item is LegacyDecisionType => item !== null);
  return normalized.length > 0 ? [...new Set(normalized)] : DEFAULT_LEGACY_DECISIONS;
}

function getRecordArray(value: unknown): GenericRecord[] {
  if (!Array.isArray(value)) return [];
  return value.map(item => toRecord(item)).filter((item): item is GenericRecord => Boolean(item));
}

function toTitle(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

// HITL cards never surface raw identifiers — they're noise to a human approver.
const UID_KEY_RE = /(^|_)(uid|guid|id)s?$/i;
const UUID_VALUE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUidEntry(key: string, value: unknown): boolean {
  if (UID_KEY_RE.test(key)) return true;
  return typeof value === 'string' && UUID_VALUE_RE.test(value.trim());
}

// Which action a set of choices leads with (the filled/primary button) vs. one
// that trails as a decline (pushed to the end so it occupies the numbered
// list's last real slot).
const AFFIRMATIVE_ACTION_WORDS = new Set([
  'confirm',
  'approve',
  'accept',
  'create',
  'continue',
  'save',
  'yes',
  'submit',
]);
const NEGATIVE_ACTION_WORDS = new Set(['reject', 'deny', 'cancel', 'ignore', 'decline', 'no']);

function matchesWordSet(action: string, words: Set<string>): boolean {
  return action.split(/[^a-z0-9]+/i).some(token => words.has(token.toLowerCase()));
}

function isAffirmativeAction(action: string): boolean {
  return matchesWordSet(action, AFFIRMATIVE_ACTION_WORDS);
}

function isNegativeAction(action: string): boolean {
  return matchesWordSet(action, NEGATIVE_ACTION_WORDS);
}

function orderLangGraphActions(actions: string[]): string[] {
  const affirmative = actions.filter(
    action => isAffirmativeAction(action) && !isNegativeAction(action)
  );
  const negative = actions.filter(isNegativeAction);
  const neutral = actions.filter(
    action => !isAffirmativeAction(action) && !isNegativeAction(action)
  );
  return [...affirmative, ...neutral, ...negative];
}

// Metadata keys whose value is a list of files (as name strings or objects).
const FILE_KEY_RE = /^(files?|file_?names?|documents?|attachments?)$/i;

function stripExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function toFileEntry(fileName: string): HumanInTheLoopDetailFile {
  return { name: stripExtension(fileName), ext: getFileExtension(fileName)?.toUpperCase() ?? null };
}

// An array is treated as "files" when the key names files (so plain name
// strings qualify) or when every entry is an object carrying a `file_name`.
// Either way we surface just the (extension-stripped) name + type, never UIDs.
function asFileArray(key: string, value: unknown[]): HumanInTheLoopDetailFile[] | null {
  const keyNamesFiles = FILE_KEY_RE.test(key);
  const files: HumanInTheLoopDetailFile[] = [];

  for (const item of value) {
    if (typeof item === 'string') {
      if (!keyNamesFiles) return null;
      const name = item.trim();
      if (!name) return null;
      files.push(toFileEntry(name));
      continue;
    }

    const record = toRecord(item);
    if (!record) return null;
    const fileName =
      getString(record, ['file_name']) ?? (keyNamesFiles ? getString(record, ['name']) : null);
    if (!fileName) return null;
    files.push(toFileEntry(fileName));
  }

  return files.length > 0 ? files : null;
}

// Collapse an object into its human-readable primitive fields, dropping ids.
function summarizeRecord(record: GenericRecord): string {
  return Object.entries(record)
    .filter(([key, value]) => isPrimitive(value) && !isUidEntry(key, value))
    .map(([, value]) => String(value))
    .join(' · ');
}

// Turn a metadata value into a structured, human-friendly shape for the card —
// never a raw JSON blob.
function buildDetailValue(key: string, rawValue: unknown): HumanInTheLoopDetailValue {
  const value = parseJsonIfString(rawValue);

  if (isPrimitive(value)) {
    return { kind: 'text', text: String(value) };
  }

  if (Array.isArray(value)) {
    const files = asFileArray(key, value);
    if (files) return { kind: 'files', files };

    const items = value
      .map(item => (isPrimitive(item) ? String(item) : summarizeRecord(toRecord(item) ?? {})))
      .filter(item => item.length > 0);
    return { kind: 'list', items };
  }

  const record = toRecord(value);
  if (record) {
    const items = Object.entries(record)
      .filter(
        ([entryKey, entryValue]) => isPrimitive(entryValue) && !isUidEntry(entryKey, entryValue)
      )
      .map(([entryKey, entryValue]) => `${toTitle(entryKey)}: ${entryValue}`);
    if (items.length > 0) return { kind: 'list', items };
  }

  return { kind: 'text', text: String(value) };
}

function detailsFromRecord(record: GenericRecord): HumanInTheLoopDetail[] {
  return Object.entries(record)
    .filter(([key, value]) => value !== undefined && value !== null && !isUidEntry(key, value))
    .map(([key, value]) => ({ label: toTitle(key), value: buildDetailValue(key, value) }));
}

// ── legacy_action (action_requests / review_configs) ─────────────────────────

function parseLegacyActionValue(
  id: string,
  valueRecord: GenericRecord
): NormalizedInterrupt | null {
  const reviewConfigByActionName = new Map<string, LegacyDecisionType[]>();
  for (const config of getRecordArray(valueRecord.review_configs)) {
    const actionName = getString(config, ['action_name']);
    if (!actionName) continue;
    reviewConfigByActionName.set(actionName, normalizeAllowedDecisions(config.allowed_decisions));
  }

  const actionRequests = getRecordArray(valueRecord.action_requests);
  if (actionRequests.length === 0) return null;

  const requests: NormalizedInterruptRequest[] = [];
  for (const [index, request] of actionRequests.entries()) {
    const actionName = getString(request, ['name']);
    if (!actionName) continue;

    const args = toRecord(request.args) ?? {};
    const description =
      getString(request, ['description']) ?? `Please review "${actionName}" before execution.`;
    const allowedDecisions = reviewConfigByActionName.get(actionName) ?? DEFAULT_LEGACY_DECISIONS;

    const choices: NormalizedChoice[] = [];
    if (allowedDecisions.includes('approve')) {
      choices.push({ key: 'approve', label: 'Approve', kind: 'plain' });
    }
    if (allowedDecisions.includes('edit')) {
      choices.push({ key: 'edit', label: 'Edit arguments', kind: 'edit-json' });
    }
    if (allowedDecisions.includes('reject')) {
      choices.push({ key: 'reject', label: 'Reject', kind: 'chat' });
    }
    if (choices.length === 0) continue;

    requests.push({
      requestKey: `${id}:${index}`,
      title: toTitle(actionName),
      description,
      details: [
        { label: 'Arguments', value: { kind: 'text', text: JSON.stringify(args, null, 2) } },
      ],
      choices,
      allowFreeform: allowedDecisions.includes('reject'),
      selectionMode: 'instant',
      actionName,
    });
  }

  if (requests.length === 0) return null;
  return {
    interruptId: id,
    shape: 'legacy_action',
    requests,
    status: 'pending',
    errorMessage: null,
    businessType: null,
  };
}

// ── langgraph_action (message + actions[] + input_schema) ───────────────────

interface LangGraphFieldSchema {
  type?: string;
  description?: string;
  default?: unknown;
  enum?: unknown[];
}

interface LangGraphActionSchema {
  required: string[];
  properties: Record<string, LangGraphFieldSchema>;
}

function normalizeLangGraphActions(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const names = value
    .map(action => {
      if (typeof action === 'string') return action.trim();
      const actionRecord = toRecord(action);
      if (!actionRecord) return '';
      return getString(actionRecord, ['name']) ?? getString(actionRecord, ['action']) ?? '';
    })
    .filter(name => name.length > 0);
  return [...new Set(names)];
}

function normalizeLangGraphInputSchema(value: unknown): Record<string, LangGraphActionSchema> {
  const inputSchemaRecord = toRecord(value) ?? {};
  const output: Record<string, LangGraphActionSchema> = {};

  for (const [actionName, rawSchema] of Object.entries(inputSchemaRecord)) {
    const schemaRecord = toRecord(rawSchema) ?? {};
    const required =
      Array.isArray(schemaRecord.required) &&
      schemaRecord.required.every(field => typeof field === 'string')
        ? schemaRecord.required
        : [];

    const rawProperties = toRecord(schemaRecord.properties) ?? {};
    const properties: Record<string, LangGraphFieldSchema> = {};
    for (const [fieldName, rawField] of Object.entries(rawProperties)) {
      const fieldRecord = toRecord(rawField);
      if (!fieldRecord) continue;
      properties[fieldName] = {
        type: typeof fieldRecord.type === 'string' ? fieldRecord.type : undefined,
        description: typeof fieldRecord.description === 'string' ? fieldRecord.description : '',
        default: fieldRecord.default,
        enum: Array.isArray(fieldRecord.enum) ? fieldRecord.enum : undefined,
      };
    }

    output[actionName] = { required, properties };
  }

  return output;
}

function isStringEnum(fieldSchema: LangGraphFieldSchema): boolean {
  return (
    Array.isArray(fieldSchema.enum) &&
    fieldSchema.enum.length > 0 &&
    fieldSchema.enum.every(option => typeof option === 'string')
  );
}

function langGraphEnumOptions(
  fieldSchema: LangGraphFieldSchema
): { label: string; value: string }[] {
  if (!isStringEnum(fieldSchema)) return [];
  return (fieldSchema.enum as string[]).map(option => ({ label: option, value: option }));
}

function shouldUseTextarea(fieldName: string): boolean {
  return /(message|description|prompt|text|content)/i.test(fieldName);
}

function isPromptCollectionAction(interruptType: string | null, actionName: string): boolean {
  if (actionName !== 'create') return false;
  return (interruptType ?? '').toLowerCase().includes('prompt_collection');
}

function buildNormalizedFormSchema(
  schema: LangGraphActionSchema,
  interruptType: string | null,
  actionName: string
): NormalizedFormSchema {
  const fields: NormalizedFormField[] = Object.entries(schema.properties).map(
    ([fieldName, fieldSchema]) => {
      const required = schema.required.includes(fieldName);
      const label = toTitle(fieldName);

      if (
        fieldName === 'collection_name' &&
        (!fieldSchema.type || fieldSchema.type === 'string') &&
        isPromptCollectionAction(interruptType, actionName)
      ) {
        return {
          name: fieldName,
          label,
          kind: 'library-collection',
          required,
          description: fieldSchema.description,
        };
      }
      if (isStringEnum(fieldSchema)) {
        return {
          name: fieldName,
          label,
          kind: 'select',
          required,
          default: fieldSchema.default,
          enumOptions: langGraphEnumOptions(fieldSchema),
          description: fieldSchema.description,
        };
      }
      if (fieldSchema.type === 'boolean') {
        return {
          name: fieldName,
          label,
          kind: 'switch',
          required,
          default: fieldSchema.default ?? false,
          description: fieldSchema.description,
        };
      }
      if (fieldSchema.type === 'integer' || fieldSchema.type === 'number') {
        return {
          name: fieldName,
          label,
          kind: 'number',
          required,
          default: fieldSchema.default,
          description: fieldSchema.description,
        };
      }
      if (fieldSchema.type === 'array') {
        return {
          name: fieldName,
          label,
          kind: 'json-array',
          required,
          default: fieldSchema.default ?? '[]',
          description: fieldSchema.description,
        };
      }
      if (fieldSchema.type === 'object') {
        return {
          name: fieldName,
          label,
          kind: 'json-object',
          required,
          default: fieldSchema.default ?? '{}',
          description: fieldSchema.description,
        };
      }
      return {
        name: fieldName,
        label,
        kind: shouldUseTextarea(fieldName) ? 'textarea' : 'text',
        required,
        default: fieldSchema.default ?? '',
        description: fieldSchema.description,
      };
    }
  );

  return { fields };
}

function getLangGraphSchemaForAction(
  inputSchemaByAction: Record<string, LangGraphActionSchema | undefined>,
  actionName: string
): LangGraphActionSchema | null {
  const schema = inputSchemaByAction[actionName];
  if (!schema) return null;
  if (Object.keys(schema.properties).length === 0) return null;
  return schema;
}

function parseLangGraphActionValue(
  id: string,
  valueRecord: GenericRecord
): NormalizedInterrupt | null {
  const message = getString(valueRecord, ['message']);
  const actionNames = normalizeLangGraphActions(valueRecord.actions);
  if (!message || actionNames.length === 0) return null;

  const interruptType = getString(valueRecord, ['type']);
  const inputSchemaByAction = normalizeLangGraphInputSchema(valueRecord.input_schema);

  const reservedKeys = new Set(['type', 'message', 'actions', 'input_schema']);
  const metadata = Object.fromEntries(
    Object.entries(valueRecord).filter(
      ([key]) => !reservedKeys.has(key) && valueRecord[key] !== undefined
    )
  );

  const choices: NormalizedChoice[] = orderLangGraphActions(actionNames).map(actionName => {
    const schema = getLangGraphSchemaForAction(inputSchemaByAction, actionName);
    if (!schema) {
      return { key: actionName, label: toTitle(actionName), kind: 'plain' };
    }
    return {
      key: actionName,
      label: toTitle(actionName),
      kind: 'schema-form',
      schema: buildNormalizedFormSchema(schema, interruptType, actionName),
    };
  });

  return {
    interruptId: id,
    shape: 'langgraph_action',
    requests: [
      {
        requestKey: `${id}:0`,
        title: message,
        description: '',
        details: detailsFromRecord(metadata),
        choices,
        allowFreeform: false,
        selectionMode: 'instant',
      },
    ],
    status: 'pending',
    errorMessage: null,
    businessType: interruptType,
  };
}

// ── tool_confirmation (ToolConfirmationInterrupt) ────────────────────────────

const DEFAULT_TOOL_CONFIRMATION_CHOICES: { value: string; label: string }[] = [
  { value: 'confirm', label: 'Confirm' },
  { value: 'deny', label: 'Deny' },
];

function parseToolConfirmationValue(
  id: string,
  valueRecord: GenericRecord
): NormalizedInterrupt | null {
  if (getString(valueRecord, ['type']) !== 'tool_confirmation') return null;
  const message = getString(valueRecord, ['message']);
  if (!message) return null;

  const dataRecord = toRecord(valueRecord.data) ?? {};
  const rawChoices = getRecordArray(valueRecord.choices)
    .map(choice => ({
      value: getString(choice, ['value']) ?? '',
      label: getString(choice, ['label']) ?? '',
    }))
    .filter(choice => choice.value.length > 0);

  const choices: NormalizedChoice[] = (
    rawChoices.length > 0 ? rawChoices : DEFAULT_TOOL_CONFIRMATION_CHOICES
  ).map(choice => ({ key: choice.value, label: choice.label || choice.value, kind: 'plain' }));
  // Always offered regardless of `choices` — the backend accepts a bare
  // {message} answer (no choice) as a valid clarification/deny-with-feedback.
  choices.push({
    key: CHAT_CHOICE_KEY,
    label: 'Chat about this',
    kind: 'chat',
    isChatOption: true,
  });

  return {
    interruptId: id,
    shape: 'tool_confirmation',
    requests: [
      {
        requestKey: `${id}:0`,
        title: message,
        description: '',
        details: detailsFromRecord(dataRecord),
        choices,
        allowFreeform: true,
        selectionMode: 'instant',
      },
    ],
    status: 'pending',
    errorMessage: null,
    businessType: null,
  };
}

// ── user_question (UserQuestionInterrupt) ────────────────────────────────────

function parseUserQuestionValue(
  id: string,
  valueRecord: GenericRecord
): NormalizedInterrupt | null {
  if (getString(valueRecord, ['type']) !== 'user_question') return null;
  const question = getString(valueRecord, ['question']);
  if (!question) return null;

  const choices: NormalizedChoice[] = getRecordArray(valueRecord.options)
    .map(option => ({
      value: getString(option, ['value']) ?? '',
      label: getString(option, ['label']) ?? '',
      data: getString(option, ['data']),
    }))
    .filter(option => option.value.length > 0)
    .map(option => ({
      key: option.value,
      label: option.label || option.value,
      kind: 'plain' as const,
      data: option.data,
    }));
  // Always offered: `UserQuestionAnswer.message` is a valid standalone answer, so
  // the user is never cornered into one of the assistant's guesses.
  choices.push({
    key: CHAT_CHOICE_KEY,
    label: 'Answer in your own words…',
    kind: 'chat',
    isChatOption: true,
  });

  return {
    interruptId: id,
    shape: 'user_question',
    requests: [
      {
        requestKey: `${id}:0`,
        title: question,
        description: '',
        // No top-level `data` on this interrupt — it lives per option instead,
        // previewed next to the choice list rather than in the detail table.
        details: [],
        choices,
        allowFreeform: true,
        selectionMode: 'confirm',
      },
    ],
    status: 'pending',
    errorMessage: null,
    businessType: null,
  };
}

// ── entry point ───────────────────────────────────────────────────────────

export function normalizeInterruptPayload(payload: unknown): NormalizedInterrupt[] {
  const normalizedPayload = parseJsonIfString(payload);
  const payloadRecord = toRecord(normalizedPayload);
  if (!payloadRecord) return [];

  const items = getRecordArray(payloadRecord.interrupts);
  const result: NormalizedInterrupt[] = [];

  for (const [index, item] of items.entries()) {
    const id = getString(item, ['id']) ?? `interrupt-${index}`;
    const valueRecord = toRecord(item.value);
    if (!valueRecord) continue;

    // document_tool interrupts are auto-resolved via Office.js elsewhere and
    // must stay invisible to the user — never render them as a choice list.
    if (getString(valueRecord, ['type']) === 'document_tool') continue;

    const parsed =
      parseUserQuestionValue(id, valueRecord) ??
      parseToolConfirmationValue(id, valueRecord) ??
      parseLegacyActionValue(id, valueRecord) ??
      parseLangGraphActionValue(id, valueRecord);
    if (parsed) result.push(parsed);
  }

  return result;
}

// ── form helpers shared by the schema-form choice kind ───────────────────────

export function buildInitialFormValues(schema: NormalizedFormSchema): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const field of schema.fields) {
    if (field.default !== undefined) {
      values[field.name] = field.default;
      continue;
    }
    switch (field.kind) {
      case 'switch': {
        values[field.name] = false;
        break;
      }
      case 'json-array': {
        values[field.name] = '[]';
        break;
      }
      case 'json-object': {
        values[field.name] = '{}';
        break;
      }
      default:
        values[field.name] = '';
    }
  }
  return values;
}

function normalizeFormFieldValue(field: NormalizedFormField, value: unknown): unknown {
  if (field.kind === 'number') {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return value;
  }
  if (field.kind === 'json-array' || field.kind === 'json-object') {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  if (typeof value === 'string') return value.trim();
  return value;
}

function isMissingRequiredValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

export function validateAndNormalizeFormValues(
  schema: NormalizedFormSchema,
  values: Record<string, unknown>
): { isValid: boolean; normalizedInput: Record<string, unknown>; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const normalizedInput: Record<string, unknown> = {};

  for (const field of schema.fields) {
    const normalizedValue = normalizeFormFieldValue(field, values[field.name]);

    if (field.required && isMissingRequiredValue(normalizedValue)) {
      errors[field.name] = 'Required';
      continue;
    }
    if (normalizedValue === '' || normalizedValue === undefined) continue;
    if (
      (field.kind === 'json-array' || field.kind === 'json-object') &&
      typeof normalizedValue === 'string'
    ) {
      errors[field.name] = 'Must be valid JSON.';
      continue;
    }
    normalizedInput[field.name] = normalizedValue;
  }

  return { isValid: Object.keys(errors).length === 0, normalizedInput, errors };
}
